import { useEffect, useRef, useState } from "react";
import { Sparkles, Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const plain = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// Watches the draft and, after a pause in typing, asks the server for label ideas.
export default function LabelSuggestions({ title, content, labels, selected, onAccept }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState([]);
  const lastRef = useRef("");

  useEffect(() => {
    const text = `${title || ""} ${plain(content)}`.trim();
    if (text.length < 12 || text === lastRef.current) return undefined;
    const id = setTimeout(async () => {
      lastRef.current = text;
      setBusy(true);
      try {
        const { data } = await api.post("/labels/suggest", { title: title || "", content: content || "", existing: labels.map((l) => l.name) });
        setItems(data.suggestions || []);
      } catch { /* suggestions are optional */ } finally { setBusy(false); }
    }, 1800);
    return () => clearTimeout(id);
  }, [title, content, labels]);

  const lower = selected.map((s) => s.toLowerCase());
  const shown = items.filter((s) => !lower.includes(s.name.toLowerCase()) && !dismissed.includes(s.name.toLowerCase()));
  if (!busy && !shown.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="label-suggestions">
      <span className="inline-flex items-center gap-1 text-[11px] text-stone-500">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 text-amber-500" />}
        {busy && !shown.length ? "Finding label ideas…" : "Ideas:"}
      </span>
      {shown.map((s) => (
        <span key={s.name} className="inline-flex items-center rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50/70 dark:bg-amber-950/30 text-xs text-amber-800 dark:text-amber-200 overflow-hidden">
          <button type="button" onClick={() => onAccept(s)} className="inline-flex items-center gap-0.5 pl-2 pr-1.5 py-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40" data-testid={`label-suggestion-${s.name}`} title={s.existing ? "Add this label" : "Create and add this label"}>
            <Plus className="h-3 w-3" />{s.name}
          </button>
          <button type="button" onClick={() => setDismissed((d) => [...d, s.name.toLowerCase()])} className="px-1.5 py-0.5 opacity-60 hover:opacity-100" aria-label={`Dismiss ${s.name}`} data-testid={`label-suggestion-dismiss-${s.name}`}>×</button>
        </span>
      ))}
    </div>
  );
}
